<template>
  <div class="section-page">
    <!-- Title Section -->
    <CategoryTitle>
      {{ pageTitle }}
    </CategoryTitle>

    <!-- Breadcrumbs -->
    <Breadcrumbs />

    <!-- Sections List -->
    <v-container class="section-container">
      <v-row dense>
        <v-col v-for="section in sections" :key="section.id" cols="12">
          <v-card class="section-card" outlined @click="navigateToSignUp(section)">
            <div class="card-content">
              <span class="section-name">{{ section.title }}</span>
              <v-icon icon="mdi-chevron-right" class="arrow-icon"></v-icon>
            </div>
          </v-card>
          <v-divider></v-divider>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import CategoryTitle from '@/components/CategoryTitle.vue';
import { useWhiteTheme } from '@/composables/useWhiteTheme';
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore();
const router = useRouter();
const route = useRoute();

// Get IDs from both route params and query params
const groupId = computed(() => parseInt(route.params.groupId));
const categoryId = computed(() => parseInt(route.query.categoryId || '0'));
const subcategoryId = computed(() => parseInt(route.query.subcategoryId || '0'));

// Dynamic page title
const pageTitle = computed(() => {
  const selectedGroup = group.value;
  if (selectedGroup) {
    return `Choose a type of ${selectedGroup.title}`;
  }
  return 'Choose the right section for your ad';
});

// Fetch the selected group and its sections dynamically
// Fetch the selected group and its sections dynamically
const group = computed(() => {
  const groupSlug = route.params.groupSlug;
  const groups = store.state.NewCategory.groups;

  // Find group by slug
  const selectedGroup =
    groups.find((g) => g.slug === groupSlug) ||
    groups.find((g) => g.title.toLowerCase().replace(/\s+/g, '-') === groupSlug);

  if (!selectedGroup) {
    console.error('No group found for groupSlug:', groupSlug);
    return null;
  }

  // Update the store with current navigation context
  const categorySlug = route.query.category;
  const subcategorySlug = route.query.subcategory;

  if (categorySlug) {
    const category = store.state.NewCategory.categories.find((c) => c.slug === categorySlug);
    if (category) {
      store.dispatch('NewCategory/selectCategory', category);
    }
  }

  if (subcategorySlug) {
    const subcategory = store.state.NewCategory.subcategories.find(
      (s) =>
        s.slug === subcategorySlug || s.title.toLowerCase().replace(/\s+/g, '-') === subcategorySlug
    );
    if (subcategory) {
      store.dispatch('NewCategory/selectSubCategory', subcategory);
    }
  }

  store.dispatch('NewCategory/selectGroup', selectedGroup);

  return selectedGroup;
});

// Correctly access the sections from the selected group
const sections = computed(() => {
  return group.value?.sections || [];
});

// Navigate to the next step (GeneralListingSignUp)
const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-');

const navigateToSignUp = (section) => {
  if (!section || !section.id) {
    console.error('Invalid section data:', section);
    return;
  }

  console.log('Navigating to section:', section);

  // Store the selected section in Vuex
  store.dispatch('NewCategory/selectSection', section);

  // Get from store with detailed logging
  const category = store.getters['NewCategory/selectedCategory'];
  const subcategory = store.getters['NewCategory/selectedSubCategory'];
  const selectedGroup = group.value;

  console.log('Navigation data:', {
    category,
    subcategory,
    selectedGroup,
    section,
  });

  // Generate slugs with better fallbacks
  const categorySlug =
    category?.slug || slugify(category?.name || '') || route.query.category || 'default-category';

  const subcategorySlug =
    subcategory?.slug ||
    slugify(subcategory?.title || '') ||
    route.query.subcategory ||
    'default-subcategory';

  const groupSlug =
    selectedGroup?.slug ||
    slugify(selectedGroup?.title || '') ||
    route.params.groupSlug ||
    'default-group';

  const sectionSlug = section.slug || slugify(section.title || '') || `section-${section.id}`;

  console.log('Generated slugs:', {
    categorySlug,
    subcategorySlug,
    groupSlug,
    sectionSlug,
  });

  // Always have a fallback, never return with error
  router.push({
    name: 'GeneralListingSignUpForJob',
    params: {
      categorySlug,
      subcategorySlug,
      groupSlug,
      sectionSlug,
    },
  });
};

onMounted(() => {
  console.log('Component mounted with:', {
    groupId: groupId.value,
    categoryId: categoryId.value,
    subcategoryId: subcategoryId.value,
  });

  // Pre-fetch sections if needed
  if (groupId.value && (!sections.value || sections.value.length === 0)) {
    store.dispatch('NewCategory/fetchSections', groupId.value);
  }

  // Set the white theme to pure white
  const { setWhiteShade } = useWhiteTheme();
  setWhiteShade('pure');
});
</script>
<style scoped>
/* General Page Styling */
.section-page {
  max-width: 50%;
  margin: 0 auto;
  padding: 16px;
  background-color: var(--white-pure) !important; /* Use pure white */
}

@media (max-width: 1024px) {
  .section-page {
    max-width: 75%;
  }
}

@media (max-width: 768px) {
  .section-page {
    max-width: 90%;
  }
}

/* Mobile Styles - Optimized for mobile view */
@media (max-width: 600px) {
  .section-page {
    max-width: 100%;
    padding: 6px 4px;
  }

  /* Adjust container padding */
  .section-container {
    padding: 4px 2px;
  }

  /* Adjust card spacing */
  .section-card {
    margin-bottom: 8px;
    padding: 14px 12px;
  }
}

/* Section Container */
.section-container {
  padding: 16px;
}

/* Section Card Styling */
.section-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.2);
  margin-bottom: 8px; /* Add space between cards */
  border: none;
  background: white;
  border: none !important;
  outline: none !important;
  box-shadow: none !important; /* Optional: remove all shadows */
  width: 100%;
}

.section-card:hover {
  border-color: rgba(0, 0, 0, 0.4);
}

.section-card:hover .section-name {
  color: #b30000;
}

/* Card Content */
.card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* Section Name */
.section-name {
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 0.5px;
  /* Handle text overflow */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  padding-right: 12px; /* Space between text and icon */
}

/* Arrow Icon */
.arrow-icon {
  color: #757575;
  flex-shrink: 0; /* Prevent icon from shrinking */
}

/* Breadcrumb Styling */
.breadcrumbs {
  padding: 8px 0;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.breadcrumbs .v-breadcrumbs-item {
  font-weight: bold;
  color: #333;
}

.breadcrumbs .v-breadcrumbs-item:hover {
  color: #b30000;
}

.breadcrumbs .v-breadcrumbs-divider {
  color: #757575;
}

/* Mobile Adjustments for Long Text */
@media (max-width: 480px) {
  /* Allow text to wrap on very small screens */
  .section-name {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.3;
    font-size: 15px;
  }

  /* Optimize breadcrumbs for mobile */
  :deep(.v-breadcrumbs) {
    padding: 8px 4px;
    font-size: 14px;
    margin-bottom: 12px;
  }

  /* Ensure proper focus area for touch */
  .section-card {
    padding: 12px 10px;
    min-height: 44px; /* Minimum touch target size */
  }

  /* Optimize spacing for mobile */
  .section-container {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  /* Ensure icon is properly sized and aligned */
  .arrow-icon {
    font-size: 18px;
  }
}

/* Small mobile screens */
@media (max-width: 375px) {
  .section-name {
    font-size: 14px;
  }

  .section-card {
    padding: 12px 8px;
  }
}

/* Thin placeholder text for all form fields */
:deep(input::placeholder),
:deep(textarea::placeholder),
:deep(.v-input input::placeholder),
:deep(.v-text-field input::placeholder),
:deep(.v-textarea textarea::placeholder) {
  font-weight: 300 !important;
  color: #b0b0b0 !important;
  opacity: 1 !important;
}
</style>
